/**
 * Resi Pixel
 * Lead source attribution library for multifamily websites.
 *
 * Features:
 *   - Priority-based source detection with CRM-specific external codes
 *   - Dynamic source mapping from property configuration
 *   - DNI (Dynamic Number Insertion) for phone numbers
 *   - Storage management for attribution data (localStorage + cookies)
 *   - CRM-agnostic field injection
 *
 * Configuration via window.resiPixelConfig:
 *   {
 *     propertyId: '123',
 *     externalSourceField: 'marketingSourceCd',
 *     fallbackPhone: '(555) 123-4567',
 *     fallbackEmail: 'info@property.com',
 *     storage: {
 *       useLocalStorage: true,
 *       useCookies: true,
 *       duration: 30
 *     },
 *     selectors: {
 *       forms: ['[data-resi-pixel]'],
 *       phones: ['[data-resi-phone]', '.phone']
 *     },
 *     debug: false,
 *     leadSources: [...]
 *   }
 */
;(function () {
  const FORM_SELECTORS = ['form']
  const PHONE_SELECTORS = ['a[href^="tel:"]', 'p', 'div']
  const SOURCE_URL_PARAMS = ['utm_source', 'lead_source', 'source', 'src']
  const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'ttclid']
  const SOURCE_QUERY_PARAM_PREFIXES = ['utm_']
  const MAX_ATTRIBUTION_TOUCHES = 20

  const ATTRIBUTION_STORAGE_DURATION_DAYS = 30
  const ATTRIBUTION_STORAGE_KEY = 'resi_attribution_metadata'
  const ATTRIBUTION_TOUCH_STORAGE_KEY = 'resi_attribution_touches'
  const ATTRIBUTION_SOURCE_DEFAULT = 'default'
  const ATTRIBUTION_EXTERNAL_KEY_DEFAULT = 'UNKNOWN'
  const ATTRIBUTION_METHOD_DEFAULT = 'unknown'
  const ATTRIBUTION_FALLBACK_FIELD = 'marketing_source'
  const REDACTED_VALUE = '[redacted]'
  const SENSITIVE_QUERY_KEY_PATTERN =
    /(^|[_.-])(email|e-mail|phone|mobile|name|first_name|firstname|last_name|lastname|token|auth|authorization|password|passwd|pwd|secret|session|jwt|signature|code|state)($|[_.-])/i

  const ANONYMOUS_ID_STORAGE_KEY = 'resi_anonymous_id'
  const EVENT_QUEUE_STORAGE_KEY = 'resi_event_queue'
  const SESSION_STORAGE_KEY = 'resi_session'
  const ANONYMOUS_ID_STORAGE_DURATION_DAYS = 400
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000
  const MAX_EVENT_QUEUE_SIZE = 50
  let flushQueuePromise = null
  let inMemoryEventQueue = []

  const FORM_INJECTION_FIELD_ANONYMOUS_ID = 'resi_anonymous_id'
  const FORM_INJECTION_FIELD_SESSION_ID = 'resi_session_id'
  const FORM_INJECTION_FIELD_SOURCE_NAME = 'resi_source_name'
  const FORM_INJECTION_FIELD_SOURCE_KEY = 'resi_source_key'
  const FORM_INJECTION_FIELD_EXTERNAL_SOURCE_FIELD = 'resi_external_source_field'
  const FORM_INJECTION_FIELD_SOURCE_QUERY = 'resi_source_query'
  const FORM_INJECTION_FIELD_SOURCE_TIMESTAMP = 'resi_source_timestamp'
  const FORM_INJECTION_FIELD_SOURCE_METHOD = 'resi_source_method'
  const FORM_INJECTION_FIELD_ATTRIBUTION_MODEL = 'resi_attribution_model'
  const FORM_INJECTION_FIELD_FIRST_TOUCH_SOURCE_KEY = 'resi_first_touch_source_key'
  const FORM_INJECTION_FIELD_LAST_TOUCH_SOURCE_KEY = 'resi_last_touch_source_key'
  const FORM_INJECTION_FIELD_LAST_NON_DIRECT_SOURCE_KEY = 'resi_last_non_direct_source_key'
  const FORM_INJECTION_FIELD_TOUCH_ID = 'resi_touch_id'

  const SOURCE_ALIASES = {
    google_ads: 'google_paid',
    google_ad: 'google_paid',
    google_paid_search: 'google_paid',
    google_ppc: 'google_paid',
    paid_search: 'google_paid',
    ppc: 'google_paid',
    sem: 'google_paid',
    bing: 'bing_organic',
    bing_ads: 'bing_paid',
    apartments: 'apartments_com',
    'apartments.com': 'apartments_com',
    apartmentlist: 'apartment_list',
    rent: 'rent_com',
    'rent.com': 'rent_com',
    rentcafe: 'rentcafe_marketplace',
    rent_cafe: 'rentcafe_marketplace',
    for_rent: 'forrent',
    'forrent.com': 'forrent',
    facebook_ads: 'facebook',
    facebook_paid: 'facebook',
    instagram_ads: 'instagram',
    instagram_paid: 'instagram',
    twitter: 'x_twitter',
    x: 'x_twitter',
    referral: 'referral_website',
    realtor: 'other',
    'realtor.com': 'other',
    rentspree: 'other',
    mynewplace: 'other',
  }

  const REFERRER_DOMAINS = {
    google: ref => (ref.includes('/aclk') ? 'google_paid' : 'google_organic'),
    bing: 'bing_organic',
    facebook: 'facebook',
    'fb.com': 'facebook',
    instagram: 'instagram',
    tiktok: 'tiktok',
    zillow: 'zillow',
    'apartments.com': 'apartments_com',
    apartmentlist: 'apartment_list',
    zumper: 'zumper',
    rentcafe: 'rentcafe_marketplace',
    'realtor.com': 'other',
    hotpads: 'hotpads',
    trulia: 'trulia',
  }

  const CLICK_ID_SOURCES = {
    gclid: 'google_paid',
    gbraid: 'google_paid',
    wbraid: 'google_paid',
    msclkid: 'bing_paid',
    fbclid: 'facebook',
    ttclid: 'tiktok',
  }

  window.resiPixelConfig = window.resiPixelConfig || {}
  window.resiPixelConfig.reinitialize = initialize

  function getConfig() {
    const config = window.resiPixelConfig || {}

    return {
      propertyId: config.propertyId,
      externalSourceField: config.externalSourceField,
      fallbackPhone: config.fallbackPhone,
      fallbackEmail: config.fallbackEmail,
      storage: {
        useLocalStorage: config.storage?.useLocalStorage !== false,
        useCookies: config.storage?.useCookies !== false,
        duration: config.storage?.duration || ATTRIBUTION_STORAGE_DURATION_DAYS,
      },
      identity: {
        useLocalStorage:
          config.identity?.useLocalStorage ?? config.storage?.useLocalStorage !== false,
        useCookies: config.identity?.useCookies ?? config.storage?.useCookies !== false,
        duration:
          config.identity?.duration ||
          config.storage?.identityDuration ||
          ANONYMOUS_ID_STORAGE_DURATION_DAYS,
      },
      selectors: {
        forms: normalizeToArray(config.selectors?.forms || config.formSelectors, FORM_SELECTORS),
        phones: normalizeToArray(
          config.selectors?.phones || config.phoneSelectors,
          PHONE_SELECTORS
        ),
      },
      debug: config.debug || false,
      leadSources: config.leadSources || [],
      resolution: config.resolution || {},
      attributionModel: config.attributionModel || config.storage?.attributionModel || '',
      sourceObservationEndpoint: resolveSourceObservationEndpoint(config),
      eventEndpoint: resolveEventEndpoint(config),
      trackingEnabled: config.trackingEnabled !== false,
    }
  }

  function resolveEventEndpoint(config) {
    if (config.eventEndpoint) {
      return config.eventEndpoint
    }

    const baseUrl = (config.apiBaseUrl || config.baseUrl || config.resiBaseUrl || '')
      .toString()
      .replace(/\/+$/, '')

    if (!baseUrl) {
      return ''
    }

    if (baseUrl.endsWith('/api/v1')) {
      return `${baseUrl}/demand-events`
    }

    if (baseUrl.endsWith('/api')) {
      return `${baseUrl}/v1/demand-events`
    }

    return `${baseUrl}/api/v1/demand-events`
  }

  function resolveSourceObservationEndpoint(config) {
    const configuredEndpoint =
      config.sourceObservationEndpoint ||
      config.resolution?.sourceObservationEndpoint ||
      config.resolution?.observationEndpoint

    if (!configuredEndpoint) {
      return ''
    }

    if (/^https?:\/\//i.test(configuredEndpoint)) {
      return configuredEndpoint
    }

    const baseUrl = (config.apiBaseUrl || config.baseUrl || config.resiBaseUrl || '')
      .toString()
      .replace(/\/+$/, '')

    if (!baseUrl) {
      return configuredEndpoint
    }

    return `${baseUrl}${configuredEndpoint.startsWith('/') ? '' : '/'}${configuredEndpoint}`
  }

  function normalizeToArray(value, defaults) {
    if (Array.isArray(value)) return value
    if (value) return [value]
    return defaults
  }

  function normalizeSourceName(source) {
    const normalized = source?.toLowerCase().trim().replace(/-/g, '_').replace(/\s+/g, '_') || ''

    return SOURCE_ALIASES[normalized] || normalized
  }

  function normalizeExternalSourceKey(key) {
    return key?.toUpperCase() || ''
  }

  function getSourceName(leadSource) {
    return leadSource?.source || leadSource?.name
  }

  function getExternalSourceKey(leadSource) {
    return leadSource?.external_source_key || leadSource?.code
  }

  function findLeadSourceMapping(leadSources, detectedSource) {
    if (detectedSource?.mapping) return detectedSource.mapping

    if (!leadSources?.length) return null

    if (detectedSource.code) {
      const byCode = leadSources.find(
        ls =>
          normalizeExternalSourceKey(getExternalSourceKey(ls)) ===
          normalizeExternalSourceKey(detectedSource.code)
      )
      if (byCode) return byCode
    }

    const bySource = leadSources.find(
      ls => normalizeSourceName(getSourceName(ls)) === normalizeSourceName(detectedSource.source)
    )
    if (bySource) return bySource

    return leadSources.find(
      ls => normalizeSourceName(getSourceName(ls)) === ATTRIBUTION_SOURCE_DEFAULT
    )
  }

  function detectSourceWithPriority(config, collectedEvidence = null) {
    const evidence = collectedEvidence || collectSourceEvidence(config)

    const ruleSource = detectResolutionRuleSource(evidence, config)
    if (ruleSource) return ruleSource

    const directCodeSource = detectDirectCodeSource(evidence, config)
    if (directCodeSource) return directCodeSource

    const urlParamSource = detectUrlParamSource(evidence.params)
    if (urlParamSource) return urlParamSource

    const clickIdSource = detectClickIdSource(evidence)
    if (clickIdSource) return clickIdSource

    const referrerSource = detectReferrerSource(config)
    if (referrerSource) return referrerSource

    const fallbackRuleSource = detectResolutionRuleSource(evidence, config, true)
    if (fallbackRuleSource) return fallbackRuleSource

    return createSourceResult(ATTRIBUTION_SOURCE_DEFAULT, null, 6, 'fallback')
  }

  function collectSourceEvidence(config) {
    const params = new URLSearchParams(window.location.search)
    const queryParams = {}

    params.forEach((value, key) => {
      queryParams[key] = value
    })

    const clickIds = {}
    const clickIdParams = config.resolution?.clickIdParams || CLICK_ID_PARAMS

    clickIdParams.forEach(param => {
      const value = getUrlParamCaseInsensitive(params, param)
      if (value) {
        clickIds[param] = value
      }
    })

    return {
      params,
      queryParams,
      clickIds,
      externalSourceField: config.externalSourceField || '',
      externalSourceValue: config.externalSourceField
        ? getUrlParamCaseInsensitive(params, config.externalSourceField)
        : null,
      landingUrl: window.location.href,
      currentUrl: window.location.href,
      path: window.location.pathname,
      referrer: document.referrer || '',
    }
  }

  function detectResolutionRuleSource(evidence, config, fallbackOnly = false) {
    const rules = Array.isArray(config.resolution?.rules) ? config.resolution.rules : []

    for (const rule of rules) {
      const isFallbackRule = rule.type === 'fallback'
      if (fallbackOnly !== isFallbackRule) continue
      if (!ruleMatchesEvidence(rule, evidence)) continue

      return createSourceResult(
        rule.source || ATTRIBUTION_SOURCE_DEFAULT,
        rule.external_source_key,
        rule.priority || 0,
        'resolution_rule',
        rule,
        rule.type === 'fallback'
      )
    }

    return null
  }

  function ruleMatchesEvidence(rule, evidence) {
    const conditions = normalizeRuleConditions(rule)
    if (!conditions.length) return false

    const matches = conditions.map(condition => conditionMatchesEvidence(condition, evidence))

    return normalizeConditionOperator(rule) === 'any'
      ? matches.some(Boolean)
      : matches.every(Boolean)
  }

  function normalizeConditionOperator(rule) {
    const operator = rule.condition_operator || rule.conditionOperator
    return operator === 'any' ? 'any' : 'all'
  }

  function normalizeRuleConditions(rule) {
    const conditions = Array.isArray(rule.conditions)
      ? rule.conditions
          .filter(condition => condition && typeof condition === 'object')
          .map(condition => ({
            type: condition.type,
            parameter: condition.parameter,
            value: condition.value,
            pattern: condition.pattern,
          }))
          .filter(condition => condition.type)
      : []

    if (conditions.length) return conditions

    if (!rule.type) return []

    return [
      {
        type: rule.type,
        parameter: rule.parameter,
        value: rule.value,
        pattern: rule.pattern,
      },
    ]
  }

  function conditionMatchesEvidence(condition, evidence) {
    const parameter = condition.parameter
    const pattern = condition.pattern || condition.value

    if (condition.type === 'external_field_exact') {
      return (
        Boolean(condition.value || pattern) &&
        externalFieldParameterMatches(parameter, evidence) &&
        Boolean(normalizeRuleValue(evidence.externalSourceValue)) &&
        normalizeRuleValue(evidence.externalSourceValue) ===
          normalizeRuleValue(condition.value || pattern)
      )
    }

    if (condition.type === 'external_field_contains') {
      return (
        externalFieldParameterMatches(parameter, evidence) &&
        Boolean(pattern) &&
        normalizeRuleValue(evidence.externalSourceValue).includes(normalizeRuleValue(pattern))
      )
    }

    if (condition.type === 'exact_query_param') {
      return (
        Boolean(parameter && (condition.value || pattern)) &&
        normalizeRuleValue(queryParamFromEvidence(evidence, parameter)) ===
          normalizeRuleValue(condition.value || pattern)
      )
    }

    if (condition.type === 'query_param_contains') {
      return (
        Boolean(parameter && pattern) &&
        normalizeRuleValue(queryParamFromEvidence(evidence, parameter)).includes(
          normalizeRuleValue(pattern)
        )
      )
    }

    if (condition.type === 'referrer_domain') {
      return (
        Boolean(pattern) &&
        normalizeRuleValue(evidence.referrer).includes(normalizeRuleValue(pattern))
      )
    }

    if (condition.type === 'click_id_exists') {
      return Boolean(parameter && evidence.clickIds?.[parameter])
    }

    if (condition.type === 'path_contains') {
      return (
        Boolean(pattern) && normalizeRuleValue(evidence.path).includes(normalizeRuleValue(pattern))
      )
    }

    if (condition.type === 'fallback') {
      return true
    }

    return false
  }

  function externalFieldParameterMatches(parameter, evidence) {
    return (
      !parameter ||
      normalizeRuleValue(evidence.externalSourceField) === normalizeRuleValue(parameter)
    )
  }

  function queryParamFromEvidence(evidence, parameter) {
    if (!parameter) return undefined

    if (evidence.params instanceof URLSearchParams) {
      const value = getUrlParamCaseInsensitive(evidence.params, parameter)
      if (value !== null) return value
    }

    const normalizedParameter = parameter.toString().toLowerCase()

    for (const [key, value] of Object.entries(evidence.queryParams || {})) {
      if (key.toLowerCase() === normalizedParameter) {
        return value
      }
    }

    return undefined
  }

  function normalizeRuleValue(value) {
    return value?.toString().trim().toLowerCase() || ''
  }

  function detectDirectCodeSource(evidence, config) {
    if (!config.externalSourceField) return null

    const directCode = evidence.externalSourceValue
    if (!directCode) return null

    const leadSource = config.leadSources.find(
      ls =>
        normalizeExternalSourceKey(getExternalSourceKey(ls)) ===
        normalizeExternalSourceKey(directCode)
    )
    if (!leadSource) return null

    return createSourceResult(getSourceName(leadSource), directCode, 1, 'query', leadSource)
  }

  function getUrlParamCaseInsensitive(params, paramName) {
    const normalizedParamName = paramName.toLowerCase()
    for (const [key, value] of params.entries()) {
      if (key.toLowerCase() === normalizedParamName) {
        return value
      }
    }
    return null
  }

  function detectUrlParamSource(params) {
    for (let i = 0; i < SOURCE_URL_PARAMS.length; i++) {
      const param = getUrlParamCaseInsensitive(params, SOURCE_URL_PARAMS[i])
      if (param) {
        return createSourceResult(classifyUrlParamSource(params, param), null, i + 2, 'query')
      }
    }
    return null
  }

  function classifyUrlParamSource(params, source) {
    const normalizedSource = normalizeSourceName(source)
    const medium = normalizeRuleValue(getUrlParamCaseInsensitive(params, 'utm_medium'))

    if (normalizedSource === 'google') {
      return hasAnyClickId(params, ['gclid', 'gbraid', 'wbraid']) || isPaidMedium(medium)
        ? 'google_paid'
        : 'google_organic'
    }

    if (normalizedSource === 'bing') {
      return hasAnyClickId(params, ['msclkid']) || isPaidMedium(medium)
        ? 'bing_paid'
        : 'bing_organic'
    }

    return source
  }

  function hasAnyClickId(params, clickIdParams) {
    return clickIdParams.some(param => Boolean(getUrlParamCaseInsensitive(params, param)))
  }

  function isPaidMedium(medium) {
    return ['cpc', 'ppc', 'paid', 'paid_search', 'sem', 'display'].includes(medium)
  }

  function detectClickIdSource(evidence) {
    for (const [param, source] of Object.entries(CLICK_ID_SOURCES)) {
      if (evidence.clickIds?.[param]) {
        return createSourceResult(source, null, 2, 'click_id')
      }
    }

    return null
  }

  function detectReferrerSource(config) {
    const referrerSource = parseReferrer()
    return referrerSource ? createSourceResult(referrerSource, null, 5, 'referrer') : null
  }

  function createSourceResult(source, code, priority, method, mapping = null, isFallback = false) {
    return {
      source: normalizeSourceName(source),
      code: code ? normalizeExternalSourceKey(code) : null,
      priority,
      method,
      mapping,
      isFallback,
    }
  }

  function parseReferrer() {
    const ref = document.referrer.toLowerCase()
    if (!ref) return null

    for (const [domain, source] of Object.entries(REFERRER_DOMAINS)) {
      if (ref.includes(domain)) {
        return typeof source === 'function' ? source(ref) : source
      }
    }

    return null
  }

  function storeAttributionData(attributionData, config) {
    const data = createStoredAttributionData(attributionData, config)
    const storageKey = attributionStorageKey(config)

    if (config.storage.useLocalStorage) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(data))
      } catch (e) {
        console.warn('[Resi Pixel] Failed to store attribution data in localStorage')
      }
    }

    if (config.storage.useCookies) {
      const cookieValue = `${storageKey}=${encodeURIComponent(JSON.stringify(data))}; path=/; max-age=${config.storage.duration * 24 * 60 * 60}`
      document.cookie = cookieValue
    }
  }

  function createStoredAttributionData(attributionData, config) {
    return {
      source: attributionData.source,
      external_source_key: attributionData.external_source_key,
      phone: attributionData.phone,
      fallback_phone: config.fallbackPhone,
      method: attributionData.method,
      query: attributionData.query,
      property_id: config.propertyId,
      touch_id: attributionData.touch_id,
      source_evidence: attributionData.source_evidence,
      detected_source: attributionData.detected_source,
      lead_source_mapping: attributionData.lead_source_mapping,
      timestamp: attributionData.timestamp || Date.now(),
      expires: Date.now() + config.storage.duration * 24 * 60 * 60 * 1000,
    }
  }

  function readStoredAttributionData(config) {
    const data = readFromLocalStorage(config) || readFromCookies(config)
    return isDataExpired(data) ? null : normalizeStoredAttributionData(data)
  }

  function normalizeStoredAttributionData(data) {
    if (!data) return null

    return {
      ...data,
      source: normalizeSourceName(data.source),
      external_source_key: normalizeExternalSourceKey(data.external_source_key),
    }
  }

  function readFromLocalStorage(config) {
    if (!config.storage.useLocalStorage) return null

    const scoped = parseStoredJson(localStorage.getItem(attributionStorageKey(config)))
    if (scoped) return scoped

    const legacy = parseStoredJson(localStorage.getItem(ATTRIBUTION_STORAGE_KEY))
    if (legacy && canUseStoredAttributionForProperty(legacy, config)) {
      localStorage.setItem(
        attributionStorageKey(config),
        JSON.stringify({
          ...legacy,
          property_id: config.propertyId,
        })
      )
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
      return legacy
    }

    return null
  }

  function readFromCookies(config) {
    if (!config.storage.useCookies) return null

    const scopedCookie = readCookieJson(attributionStorageKey(config))
    if (scopedCookie) return scopedCookie

    const legacyCookie = readCookieJson(ATTRIBUTION_STORAGE_KEY)
    if (legacyCookie && canUseStoredAttributionForProperty(legacyCookie, config)) {
      writeCookieValue(
        attributionStorageKey(config),
        JSON.stringify({ ...legacyCookie, property_id: config.propertyId }),
        config.storage.duration
      )
      deleteCookieValue(ATTRIBUTION_STORAGE_KEY)
      return legacyCookie
    }

    return null
  }

  function attributionStorageKey(config) {
    return scopedStorageKey(ATTRIBUTION_STORAGE_KEY, config.propertyId)
  }

  function attributionTouchStorageKey(config) {
    return scopedStorageKey(ATTRIBUTION_TOUCH_STORAGE_KEY, config.propertyId)
  }

  function scopedStorageKey(baseKey, propertyId) {
    if (!propertyId) return baseKey
    return `${baseKey}_${propertyId.toString().replace(/[^A-Za-z0-9_-]/g, '_')}`
  }

  function canUseStoredAttributionForProperty(data, config) {
    if (!config.propertyId) return true

    return data.property_id === config.propertyId
  }

  function parseStoredJson(stored) {
    if (!stored) return null

    try {
      return JSON.parse(stored)
    } catch (e) {
      console.warn('[Resi Pixel] Failed to parse stored attribution data')
      return null
    }
  }

  function readCookieJson(key) {
    const raw = readCookieValue(key)
    if (!raw) return null
    return parseStoredJson(raw)
  }

  function isDataExpired(data) {
    return data?.expires && Date.now() > data.expires
  }

  function readAttributionTouchState(config) {
    if (!config.storage.useLocalStorage) {
      return emptyTouchState()
    }

    const stored = readLocalStorage(attributionTouchStorageKey(config))
    const parsed = parseStoredJson(stored)

    if (!parsed || typeof parsed !== 'object') {
      return emptyTouchState()
    }

    return {
      first_touch: parsed.first_touch || null,
      last_touch: parsed.last_touch || null,
      last_non_direct_touch: parsed.last_non_direct_touch || null,
      current_session_touch: parsed.current_session_touch || null,
      touches: Array.isArray(parsed.touches) ? parsed.touches.slice(-MAX_ATTRIBUTION_TOUCHES) : [],
    }
  }

  function writeAttributionTouchState(state, config) {
    if (!config.storage.useLocalStorage) return true

    return writeLocalStorage(attributionTouchStorageKey(config), JSON.stringify(state))
  }

  function updateAttributionTouchState(attributionData, detectedSource, evidence, config) {
    const existing = readAttributionTouchState(config)
    const touch = createAttributionTouch(attributionData, detectedSource, evidence, config)
    const touches = [...existing.touches, touch].slice(-MAX_ATTRIBUTION_TOUCHES)

    const state = {
      first_touch: existing.first_touch || touch,
      last_touch: touch,
      last_non_direct_touch: isDirectTouch(touch) ? existing.last_non_direct_touch : touch,
      current_session_touch: touch,
      touches,
    }

    writeAttributionTouchState(state, config)

    return state
  }

  function createAttributionTouch(attributionData, detectedSource, evidence, config) {
    return {
      id: attributionData.touch_id || generateId('touch'),
      property_id: config.propertyId,
      source: attributionData.source,
      external_source_key: attributionData.external_source_key,
      method: attributionData.method,
      priority: detectedSource?.priority,
      query: attributionData.query,
      landing_url: evidence.landingUrl,
      current_url: evidence.currentUrl,
      path: evidence.path,
      referrer: evidence.referrer,
      click_ids: evidence.clickIds,
      session_id: getSession().id,
      anonymous_id: getAnonymousId(config),
      timestamp: attributionData.timestamp || Date.now(),
    }
  }

  function serializeSourceEvidence(evidence) {
    if (!evidence) return null

    return {
      landing_url: evidence.landingUrl,
      current_url: evidence.currentUrl,
      path: evidence.path,
      referrer: evidence.referrer,
      query_params: evidence.queryParams || {},
      click_ids: evidence.clickIds || {},
      external_source_field: evidence.externalSourceField || '',
      external_source_value: evidence.externalSourceValue || '',
    }
  }

  function serializeDetectedSource(detectedSource) {
    if (!detectedSource) return null

    return {
      source: detectedSource.source,
      code: detectedSource.code,
      method: detectedSource.method,
      priority: detectedSource.priority,
      is_fallback: Boolean(detectedSource.isFallback),
      rule:
        detectedSource.mapping && detectedSource.method === 'resolution_rule'
          ? serializeResolutionRule(detectedSource.mapping)
          : null,
    }
  }

  function serializeResolutionRule(rule) {
    if (!rule) return null

    return {
      id: rule.id,
      type: rule.type,
      parameter: rule.parameter,
      value: rule.value,
      pattern: rule.pattern,
      condition_operator: normalizeConditionOperator(rule),
      conditions: normalizeRuleConditions(rule),
      priority: rule.priority,
      lead_source_id: rule.lead_source_id,
      external_source_key: rule.external_source_key,
      source: rule.source,
    }
  }

  function serializeLeadSourceMapping(mapping) {
    if (!mapping) return null

    return {
      id: mapping.id,
      source: normalizeSourceName(getSourceName(mapping)),
      external_source_key: normalizeExternalSourceKey(getExternalSourceKey(mapping)),
      phone: mapping.phone || '',
      email: mapping.email || '',
    }
  }

  function createHiddenFieldSnapshot(
    attributionData,
    config,
    touchState = readAttributionTouchState(config)
  ) {
    if (!attributionData) return {}

    const fieldName = config.externalSourceField || ATTRIBUTION_FALLBACK_FIELD
    const queryString = attributionData.query || window.location.search.substring(1)
    const timestamp = attributionData.timestamp?.toString() || Date.now().toString()
    const method = attributionData.method || ATTRIBUTION_METHOD_DEFAULT
    const session = getSession()

    return {
      [fieldName]: attributionData.external_source_key || '',
      [FORM_INJECTION_FIELD_ANONYMOUS_ID]: getAnonymousId(config),
      [FORM_INJECTION_FIELD_SESSION_ID]: session.id,
      [FORM_INJECTION_FIELD_SOURCE_NAME]: attributionData.source || '',
      [FORM_INJECTION_FIELD_SOURCE_KEY]: attributionData.external_source_key || '',
      [FORM_INJECTION_FIELD_EXTERNAL_SOURCE_FIELD]: config.externalSourceField || '',
      [FORM_INJECTION_FIELD_SOURCE_QUERY]: queryString,
      [FORM_INJECTION_FIELD_SOURCE_TIMESTAMP]: timestamp,
      [FORM_INJECTION_FIELD_SOURCE_METHOD]: method,
      [FORM_INJECTION_FIELD_ATTRIBUTION_MODEL]: config.attributionModel || '',
      [FORM_INJECTION_FIELD_FIRST_TOUCH_SOURCE_KEY]:
        touchState.first_touch?.external_source_key || '',
      [FORM_INJECTION_FIELD_LAST_TOUCH_SOURCE_KEY]:
        touchState.last_touch?.external_source_key || attributionData.external_source_key || '',
      [FORM_INJECTION_FIELD_LAST_NON_DIRECT_SOURCE_KEY]:
        touchState.last_non_direct_touch?.external_source_key || '',
      [FORM_INJECTION_FIELD_TOUCH_ID]: attributionData.touch_id || '',
    }
  }

  function emptyTouchState() {
    return {
      first_touch: null,
      last_touch: null,
      last_non_direct_touch: null,
      current_session_touch: null,
      touches: [],
    }
  }

  function isDirectTouch(touch) {
    return (
      normalizeSourceName(touch?.source) === ATTRIBUTION_SOURCE_DEFAULT ||
      normalizeSourceName(touch?.source) === 'direct' ||
      touch?.method === 'fallback'
    )
  }

  function readLocalStorage(key) {
    try {
      return localStorage.getItem(key)
    } catch (e) {
      return null
    }
  }

  function writeLocalStorage(key, value) {
    try {
      localStorage.setItem(key, value)
      return true
    } catch (e) {
      return false
    }
  }

  function readCookieValue(key) {
    const match = document.cookie.match(`(^|;)\\s*${key}\\s*=\\s*([^;]+)`)
    if (!match) return null

    try {
      return decodeURIComponent(match[2])
    } catch (e) {
      return null
    }
  }

  function writeCookieValue(key, value, durationDays) {
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : ''
      document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${
        durationDays * 24 * 60 * 60
      }; SameSite=Lax${secure}`

      return true
    } catch (e) {
      return false
    }
  }

  function deleteCookieValue(key) {
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  }

  function readSessionStorage(key) {
    try {
      return sessionStorage.getItem(key)
    } catch (e) {
      return null
    }
  }

  function writeSessionStorage(key, value) {
    try {
      sessionStorage.setItem(key, value)
      return true
    } catch (e) {
      return false
    }
  }

  function generateId(prefix) {
    const randomId =
      window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    return `${prefix}_${randomId}`
  }

  function isValidGeneratedId(id, prefix) {
    return typeof id === 'string' && new RegExp(`^${prefix}_[A-Za-z0-9._-]{8,}$`).test(id)
  }

  function getAnonymousId(config = getConfig()) {
    const storedId = readStoredAnonymousId(config)

    if (storedId) {
      persistAnonymousId(storedId, config)
      return storedId
    }

    const anonymousId = generateId('anon')
    persistAnonymousId(anonymousId, config)

    return anonymousId
  }

  function readStoredAnonymousId(config) {
    const localId = config.identity.useLocalStorage
      ? readLocalStorage(ANONYMOUS_ID_STORAGE_KEY)
      : null

    if (isValidGeneratedId(localId, 'anon')) {
      return localId
    }

    const cookieId = config.identity.useCookies ? readCookieValue(ANONYMOUS_ID_STORAGE_KEY) : null

    if (isValidGeneratedId(cookieId, 'anon')) {
      return cookieId
    }

    if (isValidGeneratedId(window.__resiPixelAnonymousId, 'anon')) {
      return window.__resiPixelAnonymousId
    }

    return null
  }

  function persistAnonymousId(anonymousId, config) {
    window.__resiPixelAnonymousId = anonymousId

    if (config.identity.useLocalStorage) {
      writeLocalStorage(ANONYMOUS_ID_STORAGE_KEY, anonymousId)
    }

    if (config.identity.useCookies) {
      writeCookieValue(ANONYMOUS_ID_STORAGE_KEY, anonymousId, config.identity.duration)
    }
  }

  function getSession() {
    const now = Date.now()
    const memorySession = window.__resiPixelSession || null
    const storedSession = parseStoredSession(readSessionStorage(SESSION_STORAGE_KEY))
    let session = isActiveSession(memorySession, now) ? memorySession : storedSession

    if (!isActiveSession(session, now)) {
      session = {
        id: generateId('sess'),
        startedAt: now,
      }
    }

    session.lastSeen = now
    window.__resiPixelSession = session
    writeSessionStorage(SESSION_STORAGE_KEY, JSON.stringify(session))

    return session
  }

  function parseStoredSession(stored) {
    if (!stored) return null

    try {
      return JSON.parse(stored)
    } catch (e) {
      return null
    }
  }

  function isActiveSession(session, now = Date.now()) {
    return (
      isValidGeneratedId(session?.id, 'sess') && now - (session.lastSeen || 0) <= SESSION_TIMEOUT_MS
    )
  }

  function sanitizeEventDetail(value, seen = new WeakSet()) {
    if (typeof value === 'function') {
      return undefined
    }

    if (typeof Node !== 'undefined' && value instanceof Node) {
      return undefined
    }

    if (Array.isArray(value)) {
      return value.map(item => sanitizeEventDetail(item, seen)).filter(item => item !== undefined)
    }

    if (!value || typeof value !== 'object') {
      return value
    }

    if (seen.has(value)) {
      return undefined
    }
    seen.add(value)

    const sanitized = {}
    const blockedKeys = ['email', 'phone', 'firstname', 'lastname', 'name']

    Object.entries(value).forEach(([key, nestedValue]) => {
      const normalizedKey = key.toLowerCase().replace(/[-_]/g, '')
      if (blockedKeys.includes(normalizedKey)) return

      const sanitizedValue = sanitizeEventDetail(nestedValue, seen)
      if (sanitizedValue !== undefined) {
        sanitized[key] = sanitizedValue
      }
    })

    return sanitized
  }

  function numberFromPayload(payload, keys) {
    for (const key of keys) {
      const value = payload[key]
      if (value !== undefined && value !== null && value !== '' && !Number.isNaN(Number(value))) {
        return Number(value)
      }
    }

    return undefined
  }

  function stringFromPayload(payload, keys) {
    for (const key of keys) {
      const value = payload[key]
      if ((typeof value === 'string' || typeof value === 'number') && `${value}`.trim() !== '') {
        return `${value}`.trim()
      }
    }

    return undefined
  }

  function enrichEventPayload(eventType, payload = {}, config = getConfig()) {
    const attributionData = readStoredAttributionData(config)
    const session = getSession()
    const metadata = sanitizeEventDetail(payload.metadata || {})
    const propertyId =
      stringFromPayload(payload, ['property_id', 'propertyId']) || config.propertyId
    const unitId =
      stringFromPayload(payload, ['unit_id', 'unitId', 'residence_id', 'residenceId']) ||
      stringFromPayload(payload.unit || {}, ['id', 'unit_id', 'unitId'])
    const floorPlanId =
      stringFromPayload(payload, ['floor_plan_id', 'floorPlanId', 'floorplan_id']) ||
      stringFromPayload(payload.floorPlan || {}, ['id', 'floor_plan_id', 'floorPlanId']) ||
      stringFromPayload(payload.unit?.floorPlan || {}, ['id', 'floor_plan_id', 'floorPlanId'])
    const moveInDate = stringFromPayload(payload, [
      'move_in_date',
      'moveInDate',
      'start_date',
      'startDate',
    ])
    const leaseTermMonths = numberFromPayload(payload, [
      'lease_term_months',
      'leaseTermMonths',
      'term',
    ])
    const displayedBaseRent = numberFromPayload(payload, [
      'displayed_base_rent',
      'displayedBaseRent',
      'base_rent',
      'baseRent',
      'rent',
    ])
    const displayedTmlp = numberFromPayload(payload, ['displayed_tmlp', 'displayedTmlp', 'tmlp'])
    const displayedFeeTotal = numberFromPayload(payload, [
      'displayed_fee_total',
      'displayedFeeTotal',
      'fee_total',
      'feeTotal',
    ])

    return removeUndefinedValues({
      event_type: eventType,
      property_id: propertyId,
      occurred_at: new Date().toISOString(),
      form_id: stringFromPayload(payload, ['form_id', 'formId']),
      lead_id: stringFromPayload(payload, ['lead_id', 'leadId']),
      unit_id: unitId,
      floor_plan_id: floorPlanId,
      lead_source_id: payload.lead_source_id || payload.leadSourceId,
      lead_source_name: stringFromPayload(payload, ['lead_source_name', 'leadSourceName']),
      resi_source_name:
        stringFromPayload(payload, ['resi_source_name', 'resiSourceName']) ||
        attributionData?.source,
      resi_source_key:
        stringFromPayload(payload, ['resi_source_key', 'resiSourceKey']) ||
        attributionData?.external_source_key,
      resi_external_source_field: config.externalSourceField,
      resi_source_query: attributionData?.query || window.location.search.substring(1),
      resi_source_timestamp: attributionData?.timestamp?.toString(),
      resi_source_method: attributionData?.method,
      session_id: stringFromPayload(payload, ['session_id', 'sessionId']) || session.id,
      anonymous_id:
        stringFromPayload(payload, ['anonymous_id', 'anonymousId', 'client_id']) ||
        getAnonymousId(),
      move_in_date: moveInDate,
      lease_term_months: leaseTermMonths,
      displayed_base_rent: displayedBaseRent,
      displayed_tmlp: displayedTmlp,
      displayed_fee_total: displayedFeeTotal,
      metadata: {
        ...metadata,
        event_payload: sanitizeEventDetail(payload),
        page: {
          url: window.location.href,
          path: window.location.pathname,
          referrer: document.referrer || '',
          title: document.title || '',
        },
      },
    })
  }

  function removeUndefinedValues(value) {
    return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined))
  }

  function canTrack(config) {
    return Boolean(config.trackingEnabled && config.eventEndpoint && config.propertyId)
  }

  function track(eventType, payload = {}, options = {}) {
    const config = getConfig()

    if (!eventType || !canTrack(config)) {
      if (config.debug) {
        console.warn('[Resi Pixel] Demand event tracking skipped', {
          eventType,
          eventEndpoint: config.eventEndpoint,
          propertyId: config.propertyId,
        })
      }

      return Promise.resolve(false)
    }

    const eventPayload = enrichEventPayload(eventType, payload, config)

    if (options.queueOnly) {
      return Promise.resolve(queueEvent(eventPayload, config))
    }

    return sendEvent(eventPayload, config).then(sent => {
      if (!sent) {
        queueEvent(eventPayload, config)
      }

      return sent
    })
  }

  function sendEvent(payload, config) {
    const body = JSON.stringify(payload)

    if (navigator.sendBeacon && typeof Blob === 'function') {
      const blob = new Blob([body], { type: 'application/json' })

      if (navigator.sendBeacon(config.eventEndpoint, blob)) {
        return Promise.resolve(true)
      }
    }

    if (!window.fetch) {
      return Promise.resolve(false)
    }

    return fetch(config.eventEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    })
      .then(response => response.ok)
      .catch(() => false)
  }

  function canSendSourceObservation(config) {
    return Boolean(config.trackingEnabled && config.sourceObservationEndpoint && config.propertyId)
  }

  function sanitizeEvidenceForObservation(evidence, config) {
    return {
      ...evidence,
      landingUrl: sanitizeUrlForObservation(evidence.landingUrl, config),
      currentUrl: sanitizeUrlForObservation(evidence.currentUrl, config),
      referrer: sanitizeUrlForObservation(evidence.referrer, config),
      queryParams: sanitizeQueryParamsForObservation(evidence.queryParams || {}, config),
    }
  }

  function sanitizeUrlForObservation(rawUrl, config) {
    if (!rawUrl) return rawUrl

    try {
      const url = new URL(rawUrl, window.location.origin)
      const sanitizedParams = sanitizeQueryParamsForObservation(
        Object.fromEntries(url.searchParams.entries()),
        config
      )

      url.search = ''
      Object.entries(sanitizedParams).forEach(([key, value]) => {
        url.searchParams.set(key, value)
      })
      url.hash = ''

      return url.toString()
    } catch (e) {
      return rawUrl.toString().split(/[?#]/)[0]
    }
  }

  function sanitizeQueryParamsForObservation(queryParams, config) {
    const allowedParams = sourceObservationQueryParamAllowlist(config)
    const sanitizedParams = {}

    Object.entries(queryParams || {}).forEach(([key, value]) => {
      if (!shouldSendObservationQueryParam(key, allowedParams)) return

      sanitizedParams[key] = isSensitiveQueryKey(key) ? REDACTED_VALUE : sanitizeQueryValue(value)
    })

    return sanitizedParams
  }

  function sourceObservationQueryParamAllowlist(config) {
    const configuredClickIds = config.resolution?.clickIdParams || CLICK_ID_PARAMS
    const names = new Set(
      [...SOURCE_URL_PARAMS, ...configuredClickIds].map(name => name.toLowerCase())
    )

    if (config.externalSourceField) {
      names.add(config.externalSourceField.toLowerCase())
    }

    const rules = Array.isArray(config.resolution?.rules) ? config.resolution.rules : []
    rules.forEach(rule => {
      normalizeRuleConditions(rule).forEach(condition => {
        if (
          ['exact_query_param', 'query_param_contains', 'click_id_exists'].includes(
            condition.type
          ) &&
          condition.parameter
        ) {
          names.add(condition.parameter.toLowerCase())
        }
      })
    })

    return names
  }

  function shouldSendObservationQueryParam(key, allowedParams) {
    const normalizedKey = key.toLowerCase()

    return (
      SOURCE_QUERY_PARAM_PREFIXES.some(prefix => normalizedKey.startsWith(prefix)) ||
      allowedParams.has(normalizedKey)
    )
  }

  function isSensitiveQueryKey(key) {
    return SENSITIVE_QUERY_KEY_PATTERN.test(key)
  }

  function sanitizeQueryValue(value) {
    if (Array.isArray(value)) {
      return value.map(sanitizeQueryValue)
    }

    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, nestedValue]) => [
          key,
          isSensitiveQueryKey(key) ? REDACTED_VALUE : sanitizeQueryValue(nestedValue),
        ])
      )
    }

    return value
  }

  function sendSourceObservation(
    evidence,
    attributionData,
    detectedSource,
    leadSourceMapping,
    config
  ) {
    if (!canSendSourceObservation(config)) return Promise.resolve(false)

    const sanitizedEvidence = sanitizeEvidenceForObservation(evidence, config)

    const payload = removeUndefinedValues({
      property_id: config.propertyId,
      occurred_at: new Date().toISOString(),
      session_id: getSession().id,
      anonymous_id: getAnonymousId(config),
      landing_url: sanitizedEvidence.landingUrl,
      current_url: sanitizedEvidence.currentUrl,
      path: evidence.path,
      referrer: sanitizedEvidence.referrer,
      query_params: sanitizedEvidence.queryParams,
      click_ids: evidence.clickIds,
      external_source_field: evidence.externalSourceField || undefined,
      external_source_value: evidence.externalSourceValue || undefined,
      resi_source_name: attributionData?.source,
      resi_source_key: attributionData?.external_source_key,
      match_status: sourceObservationMatchStatus(detectedSource, leadSourceMapping),
      metadata: {
        source_method: attributionData?.method,
        source_priority: detectedSource?.priority,
        pixel_detected_source: detectedSource?.source,
      },
    })

    return sendJson(config.sourceObservationEndpoint, payload)
  }

  function sourceObservationMatchStatus(detectedSource, leadSourceMapping) {
    if (leadSourceMapping) {
      return detectedSource?.isFallback ||
        normalizeSourceName(detectedSource?.source) === ATTRIBUTION_SOURCE_DEFAULT
        ? 'fallback'
        : 'matched'
    }

    return detectedSource?.isFallback ||
      normalizeSourceName(detectedSource?.source) === ATTRIBUTION_SOURCE_DEFAULT
      ? 'fallback'
      : 'unmatched'
  }

  function sendJson(endpoint, payload) {
    const body = JSON.stringify(payload)

    if (navigator.sendBeacon && typeof Blob === 'function') {
      const blob = new Blob([body], { type: 'application/json' })

      if (navigator.sendBeacon(endpoint, blob)) {
        return Promise.resolve(true)
      }
    }

    if (!window.fetch) {
      return Promise.resolve(false)
    }

    return fetch(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    })
      .then(response => response.ok)
      .catch(() => false)
  }

  function readQueuedEvents(config = getConfig()) {
    if (!config.storage.useLocalStorage) return inMemoryEventQueue

    const stored = readLocalStorage(EVENT_QUEUE_STORAGE_KEY)
    if (!stored) return inMemoryEventQueue

    try {
      const queue = JSON.parse(stored)
      if (!Array.isArray(queue)) return inMemoryEventQueue

      inMemoryEventQueue = mergeQueuedEvents(queue, inMemoryEventQueue)

      return inMemoryEventQueue
    } catch (e) {
      return inMemoryEventQueue
    }
  }

  function writeQueuedEvents(queue, config = getConfig()) {
    inMemoryEventQueue = mergeQueuedEvents(queue).slice(-MAX_EVENT_QUEUE_SIZE)

    if (!config.storage.useLocalStorage) return true

    return writeLocalStorage(EVENT_QUEUE_STORAGE_KEY, JSON.stringify(inMemoryEventQueue))
  }

  function queueEvent(payload, config) {
    const queue = readQueuedEvents(config)
    queue.push(createQueuedEvent(payload))
    const queued = writeQueuedEvents(queue, config)

    if (config.debug) {
      const message = queued
        ? '[Resi Pixel] Queued demand event for retry:'
        : '[Resi Pixel] Demand event retry queue could not be persisted:'
      console.warn(message, payload.event_type)
    }

    return queued
  }

  function createQueuedEvent(payload) {
    return {
      id: generateId('evt'),
      payload,
      queuedAt: Date.now(),
    }
  }

  function normalizeQueuedEvent(item) {
    if (!item) return null

    if (item.id && item.payload) {
      return item
    }

    if (item.event_type) {
      return createQueuedEvent(item)
    }

    return null
  }

  function mergeQueuedEvents(...queues) {
    const queuedEvents = new Map()

    queues.flat().forEach(item => {
      const queuedEvent = normalizeQueuedEvent(item)
      if (queuedEvent) {
        queuedEvents.set(queuedEvent.id, queuedEvent)
      }
    })

    return Array.from(queuedEvents.values())
  }

  function removeQueuedEvent(id, config) {
    const queue = readQueuedEvents(config)
    return writeQueuedEvents(
      queue.filter(item => item.id !== id),
      config
    )
  }

  function flushQueue() {
    if (flushQueuePromise) return flushQueuePromise

    const config = getConfig()
    if (!canTrack(config)) return Promise.resolve(false)

    const queue = readQueuedEvents(config)
    if (!queue.length) return Promise.resolve(true)

    flushQueuePromise = queue
      .reduce(
        (promise, payload) =>
          promise.then(allSent =>
            sendEvent(payload.payload, config).then(sent => {
              if (sent) {
                const removed = removeQueuedEvent(payload.id, config)

                return allSent && removed
              }

              return false
            })
          ),
        Promise.resolve(true)
      )
      .finally(() => {
        flushQueuePromise = null
      })

    return flushQueuePromise
  }

  function registerDemandEventListeners() {
    if (window.resiPixelDemandListenersRegistered) return
    window.resiPixelDemandListenersRegistered = true

    document.addEventListener('resi_demand_event', event => {
      const detail = event.detail || {}
      track(detail.event_type || detail.eventType || detail.type, detail.payload || detail)
    })

    document.addEventListener('resi_form_submission_success', event => {
      track('form_submitted', event.detail || {})
    })

    document.addEventListener('resi_residence_viewed', event => {
      track('unit_viewed', event.detail || {})
    })

    document.addEventListener('resi_application_start', event => {
      track('apply_clicked', event.detail || {})
    })

    document.addEventListener('yootheme:tour.open', event => {
      const tourConfig = event.detail?.tour?.config || window.yootheme?.tour || {}

      track('tour_modal_opened', {
        propertyId: tourConfig.propertyId,
        metadata: {
          component: 'tour_modal',
          variant: tourConfig.variant,
          source_event: 'yootheme:tour.open',
        },
      })
    })

    document.addEventListener('click', event => {
      const target = event.target?.closest?.('[data-resi-event]')
      if (!target) return

      const eventType = target.dataset.resiEvent
      if (!eventType) return

      track(eventType, payloadFromElement(target))
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushQueue()
      }
    })

    window.addEventListener('pagehide', () => {
      flushQueue()
    })
  }

  function payloadFromElement(element) {
    const payload = {
      metadata: {
        element_text: element.textContent?.trim() || '',
      },
    }

    if (element.href) {
      payload.destination_url = element.href
      payload.metadata.destination_url = element.href
    }

    Object.entries(element.dataset || {}).forEach(([key, value]) => {
      if (!key.startsWith('resi') || key === 'resiEvent') return

      const payloadKey = key.replace(/^resi/, '')
      const normalizedKey = payloadKey.charAt(0).toLowerCase() + payloadKey.slice(1)
      payload[normalizedKey] = value
    })

    return payload
  }

  function drainPreloadedEvents() {
    const queuedEvents = window.resiPixelEventQueue
    if (!Array.isArray(queuedEvents) || !queuedEvents.length) return

    window.resiPixelEventQueue = []

    queuedEvents.forEach(event => {
      if (Array.isArray(event)) {
        track(event[0], event[1] || {})
        return
      }

      track(event.eventType || event.event_type || event.type, event.payload || event)
    })
  }

  function installPublicApi() {
    const api = {
      track,
      flushQueue,
      getAttribution: () => {
        const config = getConfig()
        const attribution = readStoredAttributionData(config)

        if (!attribution) return null
        const touchState = readAttributionTouchState(config)

        return {
          ...attribution,
          dni_phone: attribution.phone || '',
          hidden_fields: createHiddenFieldSnapshot(attribution, config, touchState),
          touch_state: touchState,
        }
      },
      getSession,
      initialize,
      reinitialize: initialize,
    }

    window.ResiPixel = Object.assign(window.ResiPixel || {}, api)
    window.resiPixel = window.ResiPixel
  }

  function injectFieldIntoForm(form, fieldValue, fieldName, debug = false) {
    const existingField = form.querySelector(`input[name="${fieldName}"]`)

    if (existingField) {
      existingField.value = fieldValue || ''
      if (debug) {
        console.log(`[Resi Pixel] Updated field "${fieldName}" with value "${existingField.value}"`)
      }
      return
    }

    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = fieldName
    input.value = fieldValue || ''

    form.appendChild(input)
    if (debug) {
      console.log(`[Resi Pixel] Injected field "${fieldName}" with value "${input.value}"`)
    }
  }

  function applyAttributionToForm(form, config, attributionData) {
    if (!form || !attributionData) return

    const fieldName = config.externalSourceField || ATTRIBUTION_FALLBACK_FIELD
    const queryString = attributionData.query || window.location.search.substring(1)
    const timestamp = attributionData.timestamp?.toString() || Date.now().toString()
    const method = attributionData.method || ATTRIBUTION_METHOD_DEFAULT
    const session = getSession()
    const touchState = readAttributionTouchState(config)

    injectFieldIntoForm(form, attributionData.external_source_key, fieldName, config.debug)
    injectFieldIntoForm(
      form,
      getAnonymousId(config),
      FORM_INJECTION_FIELD_ANONYMOUS_ID,
      config.debug
    )
    injectFieldIntoForm(form, session.id, FORM_INJECTION_FIELD_SESSION_ID, config.debug)
    injectFieldIntoForm(
      form,
      attributionData.source,
      FORM_INJECTION_FIELD_SOURCE_NAME,
      config.debug
    )
    injectFieldIntoForm(
      form,
      attributionData.external_source_key,
      FORM_INJECTION_FIELD_SOURCE_KEY,
      config.debug
    )
    injectFieldIntoForm(
      form,
      config.externalSourceField || '',
      FORM_INJECTION_FIELD_EXTERNAL_SOURCE_FIELD,
      config.debug
    )
    injectFieldIntoForm(form, queryString, FORM_INJECTION_FIELD_SOURCE_QUERY, config.debug)
    injectFieldIntoForm(form, timestamp, FORM_INJECTION_FIELD_SOURCE_TIMESTAMP, config.debug)
    injectFieldIntoForm(form, method, FORM_INJECTION_FIELD_SOURCE_METHOD, config.debug)
    injectFieldIntoForm(
      form,
      config.attributionModel || '',
      FORM_INJECTION_FIELD_ATTRIBUTION_MODEL,
      config.debug
    )
    injectFieldIntoForm(
      form,
      touchState.first_touch?.external_source_key || '',
      FORM_INJECTION_FIELD_FIRST_TOUCH_SOURCE_KEY,
      config.debug
    )
    injectFieldIntoForm(
      form,
      touchState.last_touch?.external_source_key || attributionData.external_source_key,
      FORM_INJECTION_FIELD_LAST_TOUCH_SOURCE_KEY,
      config.debug
    )
    injectFieldIntoForm(
      form,
      touchState.last_non_direct_touch?.external_source_key || '',
      FORM_INJECTION_FIELD_LAST_NON_DIRECT_SOURCE_KEY,
      config.debug
    )
    injectFieldIntoForm(
      form,
      attributionData.touch_id || '',
      FORM_INJECTION_FIELD_TOUCH_ID,
      config.debug
    )
  }

  function applyDNI(attributionData, config) {
    if (!attributionData.phone) return

    const sourcePhone = attributionData.phone
    const fallbackPhone = config.fallbackPhone

    if (!isPhoneAllowedForCurrentConfig(sourcePhone, config)) {
      if (config.debug) {
        console.warn('[DNI] Stored attribution phone is not valid for the current property config')
      }
      return
    }

    if (!fallbackPhone) {
      if (config.debug) {
        console.warn('[DNI] No fallback phone configured')
      }
      return
    }

    const normalizedFallbackPhone = normalizePhoneNumber(fallbackPhone)
    const phoneTargets = findPhoneTargets(
      fallbackPhone,
      normalizedFallbackPhone,
      config.selectors.phones
    )
    const uniqueTargets = deduplicatePhoneTargets(phoneTargets)

    if (uniqueTargets.length === 0) {
      if (config.debug) {
        console.log(`[DNI] No phone elements found with fallback phone: ${fallbackPhone}`)
      }
      return
    }

    uniqueTargets.forEach(target => {
      swapPhoneNumber(target, fallbackPhone, normalizedFallbackPhone, sourcePhone, config.debug)
    })

    if (config.debug) {
      console.log(
        `[DNI] Swapped ${uniqueTargets.length} phone element(s): ${fallbackPhone} → ${sourcePhone}`
      )
    }
  }

  function isPhoneAllowedForCurrentConfig(phone, config) {
    const normalizedPhone = normalizePhoneNumber(phone)
    if (!normalizedPhone) return false

    const allowedPhones = [
      config.fallbackPhone,
      ...(config.leadSources || []).map(source => source.phone),
    ]

    return allowedPhones.some(
      allowedPhone => normalizePhoneNumber(allowedPhone) === normalizedPhone
    )
  }

  function findPhoneTargets(fallbackPhone, normalizedFallbackPhone, phoneSelectors) {
    const targets = []

    // Find elements matching selectors that contain the phone number
    const explicitElements = document.querySelectorAll(phoneSelectors.join(', '))
    explicitElements.forEach(element => {
      const elementText = element.textContent || ''
      const elementValue = element.value || element.placeholder || ''
      const elementHref = element.href || element.getAttribute('href') || ''

      const normalizedElementText = normalizePhoneNumber(elementText)
      const normalizedElementValue = normalizePhoneNumber(elementValue)
      const normalizedElementHref = normalizePhoneNumber(elementHref.replace(/^tel:/, ''))

      if (
        normalizedElementText === normalizedFallbackPhone ||
        normalizedElementValue === normalizedFallbackPhone ||
        normalizedElementHref === normalizedFallbackPhone ||
        normalizedElementText.includes(normalizedFallbackPhone) ||
        normalizedElementValue.includes(normalizedFallbackPhone) ||
        normalizedElementHref.includes(normalizedFallbackPhone)
      ) {
        targets.push({ element, textNodes: null, type: 'explicit' })
      }
    })

    // Find elements by text content using tree walker
    const textNodeTargets = findElementsWithPhone(fallbackPhone, phoneSelectors)
    targets.push(...textNodeTargets)

    // Find tel: links containing the phone number
    const telLinkElements = findTelLinksWithPhone(fallbackPhone)
    telLinkElements.forEach(element => {
      targets.push({ element, textNodes: null, type: 'tel' })
    })

    return targets
  }

  function deduplicatePhoneTargets(targets) {
    const seen = new WeakSet()
    const unique = []

    targets.forEach(target => {
      if (!seen.has(target.element)) {
        seen.add(target.element)
        unique.push(target)
      }
    })

    return unique
  }

  function normalizePhoneNumber(phone) {
    if (!phone) return ''
    return phone.replace(/\D/g, '')
  }

  function findElementsWithPhone(phoneNumber, selectors = []) {
    const targets = []
    const normalizedTargetPhone = normalizePhoneNumber(phoneNumber)

    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null, false)

    let node
    while ((node = walker.nextNode())) {
      const normalizedNodePhone = normalizePhoneNumber(node.textContent)
      if (normalizedNodePhone === normalizedTargetPhone) {
        const matchingParent = findMatchingParent(node.parentElement, selectors)
        if (matchingParent) {
          targets.push({
            element: matchingParent,
            textNodes: [node],
            type: 'text-node',
          })
        }
      }
    }

    return targets
  }

  function findMatchingParent(element, selectors) {
    while (element && element !== document.body) {
      if (selectors.some(selector => elementMatchesSelector(element, selector))) {
        return element
      }
      element = element.parentElement
    }
    return null
  }

  function elementMatchesSelector(element, selector) {
    try {
      return element.matches(selector)
    } catch (e) {
      return false
    }
  }

  function findTelLinksWithPhone(phoneNumber) {
    const normalizedTargetPhone = normalizePhoneNumber(phoneNumber)
    const telLinks = document.querySelectorAll('a[href^="tel:"]')

    return Array.from(telLinks).filter(link => {
      const telNumber = link.getAttribute('href').replace('tel:', '').trim()
      return normalizePhoneNumber(telNumber) === normalizedTargetPhone
    })
  }

  function updatePhoneInAttributes(
    element,
    fallbackPhone,
    normalizedFallbackPhone,
    sourcePhone,
    debug = false
  ) {
    let updated = false

    const href = element.getAttribute('href')
    if (href && href.startsWith('tel:')) {
      const telNumber = href.replace(/^tel:/, '').trim()
      const normalizedTelNumber = normalizePhoneNumber(telNumber)

      if (normalizedTelNumber === normalizedFallbackPhone) {
        element.setAttribute('href', `tel:${sourcePhone}`)
        updated = true
        if (debug) {
          console.log(`[DNI] Updated href attribute: tel:${telNumber} → tel:${sourcePhone}`)
        }
      }
    }

    const title = element.getAttribute('title')
    if (title) {
      const normalizedTitle = normalizePhoneNumber(title)
      if (
        normalizedTitle === normalizedFallbackPhone ||
        normalizedTitle.includes(normalizedFallbackPhone)
      ) {
        const phoneRegex = createPhoneRegex(fallbackPhone)
        phoneRegex.lastIndex = 0
        const match = phoneRegex.exec(title)
        if (match) {
          const matchStart = match.index
          const matchEnd = matchStart + match[0].length
          const matchedText = match[0]
          const leadingWhitespace = matchedText.match(/^\s*/)?.[0] || ''
          const trailingWhitespace = matchedText.match(/\s*$/)?.[0] || ''
          const beforeMatch = title.substring(0, matchStart)
          const afterMatch = title.substring(matchEnd)
          const updatedTitle =
            beforeMatch + leadingWhitespace + sourcePhone + trailingWhitespace + afterMatch

          element.setAttribute('title', updatedTitle)
          updated = true
          if (debug) {
            console.log(`[DNI] Updated title attribute: ${title} → ${updatedTitle}`)
          }
        } else if (debug) {
          console.log(
            `[DNI] Title contains phone but regex didn't match: "${title}" (fallback: "${fallbackPhone}")`
          )
        }
      } else if (debug && normalizedTitle) {
        console.log(
          `[DNI] Title doesn't contain fallback phone: "${title}" (normalized: "${normalizedTitle}", fallback: "${normalizedFallbackPhone}")`
        )
      }
    }

    return updated
  }

  function swapPhoneNumber(
    target,
    fallbackPhone,
    normalizedFallbackPhone,
    sourcePhone,
    debug = false
  ) {
    const { element, textNodes, type } = target

    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      const originalValue = element.value || element.placeholder || ''
      const normalizedOriginal = normalizePhoneNumber(originalValue)

      if (normalizedOriginal === normalizedFallbackPhone) {
        element.setAttribute('data-original-phone', originalValue)
        element.value = sourcePhone
        if (debug) {
          console.log(`[DNI] Swapped input: ${originalValue} → ${sourcePhone}`)
        }
      }
      return
    }

    if (type === 'tel' && element.tagName === 'A' && element.href?.startsWith('tel:')) {
      const hrefNumber = element.getAttribute('href').replace(/^tel:/, '').trim()
      const normalizedHref = normalizePhoneNumber(hrefNumber)

      if (normalizedHref === normalizedFallbackPhone) {
        element.setAttribute('data-original-phone', hrefNumber)
        element.setAttribute('href', `tel:${sourcePhone}`)
        replacePhoneInTextNodes(element, fallbackPhone, sourcePhone)
        updatePhoneInAttributes(element, fallbackPhone, normalizedFallbackPhone, sourcePhone, debug)
        if (debug) {
          console.log(`[DNI] Swapped tel link href: ${hrefNumber} → ${sourcePhone}`)
        }
      }
      return
    }

    if (textNodes && textNodes.length > 0) {
      const replaced = replacePhoneInTextNodes(element, fallbackPhone, sourcePhone, textNodes)
      if (replaced) {
        element.setAttribute('data-original-phone', fallbackPhone)
        updatePhoneInAttributes(element, fallbackPhone, normalizedFallbackPhone, sourcePhone, debug)
        if (debug) {
          console.log(`[DNI] Swapped text node(s): ${fallbackPhone} → ${sourcePhone}`)
        }
      }
      return
    }

    const hasChildElements = element.children.length > 0
    const elementHref = element.getAttribute('href') || ''
    const normalizedHref = normalizePhoneNumber(elementHref.replace(/^tel:/, ''))
    const originalContent = element.textContent || ''
    const normalizedOriginal = normalizePhoneNumber(originalContent)

    const hrefMatches = elementHref.startsWith('tel:') && normalizedHref === normalizedFallbackPhone
    const textMatches = normalizedOriginal === normalizedFallbackPhone

    if (hrefMatches || textMatches) {
      if (hrefMatches) {
        element.setAttribute('data-original-phone', elementHref.replace(/^tel:/, ''))
        element.setAttribute('href', `tel:${sourcePhone}`)
        if (debug) {
          console.log(`[DNI] Swapped href: ${elementHref} → tel:${sourcePhone}`)
        }
      }

      if (hasChildElements) {
        replacePhoneInTextNodes(element, fallbackPhone, sourcePhone)
      } else if (textMatches) {
        element.setAttribute('data-original-phone', originalContent)
        element.textContent = sourcePhone
      }

      updatePhoneInAttributes(element, fallbackPhone, normalizedFallbackPhone, sourcePhone, debug)

      if (debug && !hrefMatches) {
        console.log(`[DNI] Swapped element: ${fallbackPhone} → ${sourcePhone}`)
      }
    }
  }

  function replacePhoneInTextNodes(
    containerElement,
    fallbackPhone,
    sourcePhone,
    specificTextNodes = null
  ) {
    const normalizedFallback = normalizePhoneNumber(fallbackPhone)
    let replaced = false
    const textNodes = specificTextNodes || getTextNodesInElement(containerElement)

    textNodes.forEach(textNode => {
      const nodeText = textNode.textContent
      const normalizedNodeText = normalizePhoneNumber(nodeText)

      if (normalizedNodeText === normalizedFallback) {
        textNode.textContent = sourcePhone
        replaced = true
      } else if (normalizedNodeText.includes(normalizedFallback)) {
        const phoneRegex = createPhoneRegex(fallbackPhone)
        if (phoneRegex.test(nodeText)) {
          textNode.textContent = nodeText.replace(phoneRegex, sourcePhone)
          replaced = true
        }
      }
    })

    return replaced
  }

  function getTextNodesInElement(element) {
    const textNodes = []
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false)

    let node
    while ((node = walker.nextNode())) {
      textNodes.push(node)
    }

    return textNodes
  }

  function createPhoneRegex(phoneNumber) {
    // Creates a regex pattern that matches phone numbers in various formats
    // Handles: (111) 111-1111, 111-111-1111, 111.111.1111, 111 111 1111, etc.
    const digits = phoneNumber.replace(/\D/g, '')
    if (!digits) {
      return new RegExp(phoneNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')
    }

    let pattern = ''
    if (digits.length === 10) {
      // US phone format: 3-3-4 digit groups with flexible separators
      pattern = `[()\\-\\s.]*${digits.slice(0, 3)}[()\\-\\s.]*${digits.slice(3, 6)}[()\\-\\s.]*${digits.slice(6)}`
    } else {
      // Other lengths: allow flexible separators between all digits
      pattern = digits.split('').join('[()\\-\\s.]*')
    }

    return new RegExp(pattern, 'g')
  }

  function findForms(config) {
    const forms = []
    const foundIds = new Set()

    for (const selector of config.selectors.forms) {
      try {
        const elements = document.querySelectorAll(selector)
        elements.forEach(form => {
          const formId = form.id || form.outerHTML
          if (!foundIds.has(formId)) {
            forms.push(form)
            foundIds.add(formId)
          }
        })
      } catch (e) {
        continue
      }
    }

    return forms
  }

  function initialize() {
    const config = getConfig()
    let attributionData = readStoredAttributionData(config)
    let sourceContext = null

    if (shouldDetectNewSource(attributionData, config)) {
      sourceContext = detectAndStoreNewSource(config)
      attributionData = sourceContext.attributionData
    }

    const forms = findForms(config)
    logInitialization(config, attributionData, forms)

    forms.forEach(form => applyAttributionToForm(form, config, attributionData))
    applyDNI(attributionData, config)
    sendSourceObservationFromContext(sourceContext, attributionData, config)
    registerDemandEventListeners()
    flushQueue()
    drainPreloadedEvents()

    return attributionData
  }

  function shouldDetectNewSource(attributionData, config) {
    if (!attributionData) return true

    const currentQuery = window.location.search.substring(1)
    if (!currentQuery) return false

    const storedQuery = attributionData.query || ''
    return currentQuery !== storedQuery
  }

  function detectAndStoreNewSource(config) {
    const evidence = collectSourceEvidence(config)
    const detectedSource = detectSourceWithPriority(config, evidence)
    const leadSourceMapping = findLeadSourceMapping(config.leadSources, detectedSource)
    const externalSourceKey = getExternalSourceKey(leadSourceMapping)
    const timestamp = Date.now()

    const attributionData = {
      source: detectedSource.source,
      external_source_key: externalSourceKey
        ? normalizeExternalSourceKey(externalSourceKey)
        : ATTRIBUTION_EXTERNAL_KEY_DEFAULT,
      phone: leadSourceMapping?.phone || config.fallbackPhone,
      method: detectedSource.method,
      query: window.location.search.substring(1),
      touch_id: generateId('touch'),
      source_evidence: serializeSourceEvidence(evidence),
      detected_source: serializeDetectedSource(detectedSource),
      lead_source_mapping: serializeLeadSourceMapping(leadSourceMapping),
      timestamp,
    }

    updateAttributionTouchState(attributionData, detectedSource, evidence, config)
    storeAttributionData(attributionData, config)

    if (config.debug) {
      console.log('[Resi Pixel] New source detected and stored:', attributionData)
      console.log('[Resi Pixel] Detected source:', detectedSource)
      console.log('[Resi Pixel] Lead source mapping:', leadSourceMapping)
    }

    return {
      attributionData,
      detectedSource,
      leadSourceMapping,
      evidence,
    }
  }

  function sendSourceObservationFromContext(sourceContext, attributionData, config) {
    const context = sourceContext || buildStoredAttributionSourceContext(attributionData, config)

    if (!context) return

    sendSourceObservation(
      context.evidence,
      context.attributionData,
      context.detectedSource,
      context.leadSourceMapping,
      config
    )
  }

  function buildStoredAttributionSourceContext(attributionData, config) {
    if (!attributionData) return null

    const evidence = collectSourceEvidence(config)
    const detectedSource = createSourceResult(
      attributionData.source,
      attributionData.external_source_key,
      0,
      attributionData.method || 'stored',
      null,
      storedAttributionWasFallback(attributionData)
    )
    const leadSourceMapping = findLeadSourceMapping(config.leadSources, detectedSource)

    return {
      attributionData,
      detectedSource,
      leadSourceMapping,
      evidence,
    }
  }

  function storedAttributionWasFallback(attributionData) {
    const detectedSource = attributionData.detected_source || {}

    return Boolean(
      detectedSource.is_fallback ||
      detectedSource.isFallback ||
      attributionData.is_fallback ||
      attributionData.isFallback ||
      normalizeSourceName(attributionData.source) === ATTRIBUTION_SOURCE_DEFAULT ||
      attributionData.method === 'fallback'
    )
  }

  function logInitialization(config, attributionData, forms) {
    if (!config.debug) return

    if (normalizeSourceName(attributionData.source) === ATTRIBUTION_SOURCE_DEFAULT) {
      console.log('[Resi Pixel] Using stored attribution data:', attributionData)
    }

    console.group('[Resi Pixel] Initialization')
    console.log('Attribution data:', attributionData)
    console.log('Property ID:', config.propertyId)
    console.log('External source field:', config.externalSourceField)
    console.log('Forms found:', forms.length)
    console.groupEnd()
  }

  if (window.resiPixelInitialized) {
    return
  }
  window.resiPixelInitialized = true
  installPublicApi()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize)
  } else {
    initialize()
  }
})()
